import { useState } from "react";
import { Task } from "@/bryntum-lib/models/Task";
import { Button } from "@/modern/components/ui/button";
import { BaseModal } from "@/react/components/editors/base/BaseModal";
import { BaseModalBody } from "@/react/components/editors/base/BaseModalBody";
import { EntityFileProvider, useEntityFileContext, FormattedFile } from "@/react/components/entityViews/files/context/EntityFileContext";
import { FileGridView } from "@/react/components/entityViews/files/components/editors/FileGridView";
import { ENTITY_TYPES } from "@/other/constants/OtherKeys";
import { FileDetailModal } from "@/react/components/entityViews/files/components/editors/FileDetailModal/FileDetailModal";
import ImageAnnotationEditor from "@/react/components/entityViews/files/components/editors/ImageAnnotationEditor";
import { useSwivuBryntumContext } from "@/react/hooks/useSwivuBryntumContext";

type FileFilter = "photos" | "documents";

interface TaskFilesEditorProps {
  record: {
    task: Task;
    initialTab?: FileFilter;
  };
  onClose: () => void;
  editorInstance?: unknown;
}

const TaskFilesEditorContent = ({ record, onClose, editorInstance }: TaskFilesEditorProps) => {
  const { task: taskRecord, initialTab } = record;
  const [fileToAnnotate, setFileToAnnotate] = useState<FormattedFile | null>(null);
  const [navigationFiles, setNavigationFiles] = useState<FormattedFile[]>([]);

  const { openedFile, isFileEditorOpen, setIsFileEditorOpen, entityRecord } = useEntityFileContext();
  const { project } = useSwivuBryntumContext();

  const headerRenderer = () => (
    <div className="drag-handle flex items-center justify-between p-5 border-b border-border/50 rounded-t-lg bg-card">
      <div className="flex items-center gap-3">
        <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-primary/10">
          <i className="fa-solid fa-file text-primary text-lg" />
        </div>
        <div>
          <h2 className="text-lg font-semibold m-0">{taskRecord.name}</h2>
          {taskRecord?.name && <p className="text-sm text-muted-foreground m-0">Task Files</p>}
        </div>
      </div>
      <button
        onClick={onClose}
        className="absolute top-1 right-1 w-10 h-10 flex items-center justify-center bg-background rounded-full border border-border hover:bg-accent transition-colors focus:outline-none focus:ring-2 focus:ring-ring"
      >
        <i className="fa-solid fa-times text-muted-foreground" />
        <span className="sr-only">Close</span>
      </button>
    </div>
  );

  const footerRenderer = () => (
    <div className="flex items-center justify-end w-full gap-4 flex-wrap">
      <div className="flex items-center gap-2">
        <Button type="button" variant="outline" onClick={onClose}>
          Close
        </Button>
      </div>
    </div>
  );

  return (
    <>
      <BaseModal
        record={taskRecord}
        onClose={onClose}
        title=""
        description=""
        editorInstance={editorInstance}
        hideMenu={true}
        size={{
          width: "60rem",
          height: "min(100vh, 700px)",
          maxHeight: "90vh",
          maxWidth: "90vw",
          minWidth: "30rem",
          minHeight: "500px"
        }}
        modal
        version={undefined}
        swivuAssistanceConfig={undefined}
        footerRenderer={footerRenderer}
        headerRenderer={headerRenderer}
        headerIconCls=""
        className="backdrop-blur-2xl border-0"
      >
        <BaseModalBody contentElementClassName="bg-transparent" className="py-0">
          <FileGridView
            initialFilter={initialTab}
            setFileToAnnotate={setFileToAnnotate}
            onNavigationFilesChange={setNavigationFiles}
          />
        </BaseModalBody>
      </BaseModal>

      {/* File Detail Modal - Outside BaseModal */}
      {openedFile && (
        <FileDetailModal
          entityRecord={entityRecord}
          file={openedFile}
          navigationFiles={navigationFiles}
          open={isFileEditorOpen}
          setOpen={setIsFileEditorOpen}
        />
      )}

      {/* Image Annotation Editor - Outside BaseModal */}
      {fileToAnnotate && fileToAnnotate.originalImageUrl && (
        <ImageAnnotationEditor
          project={project}
          entityRecord={entityRecord}
          fileToAnnotate={fileToAnnotate}
          onClose={() => setFileToAnnotate(null)}
        />
      )}
    </>
  );
};

const SwivuTaskFilesEditorPopup = (args: TaskFilesEditorProps) => {
  return (
    <EntityFileProvider record={args.record.task} entityType={ENTITY_TYPES.TASK}>
      <TaskFilesEditorContent {...args} />
    </EntityFileProvider>
  );
};

export default SwivuTaskFilesEditorPopup;
